// src/utils/Big.ts
import Big from "big.js";
import toFormat from "toformat";
Big.strict = true;
var Big_default = toFormat(Big);

// src/utils/Decimal.ts
import Decimal from "decimal.js-light";
import toFormat2 from "toformat";
var Decimal_default = toFormat2(Decimal);

// src/utils/index.ts
import { default as default2 } from "jsbi";

// src/entities/Price.ts
import invariant4 from "tiny-invariant";

// src/entities/Fraction.ts
import JSBI from "jsbi";
import invariant from "tiny-invariant";

// src/enums/Rounding.ts
var Rounding = /* @__PURE__ */ ((Rounding2) => {
  Rounding2[Rounding2["ROUND_DOWN"] = 0] = "ROUND_DOWN";
  Rounding2[Rounding2["ROUND_HALF_UP"] = 1] = "ROUND_HALF_UP";
  Rounding2[Rounding2["ROUND_UP"] = 3] = "ROUND_UP";
  return Rounding2;
})(Rounding || {});
var Rounding_default = Rounding;

// src/entities/Fraction.ts
var toSignificantRounding = {
  [Rounding_default.ROUND_DOWN]: Decimal_default.ROUND_DOWN,
  [Rounding_default.ROUND_HALF_UP]: Decimal_default.ROUND_HALF_UP,
  [Rounding_default.ROUND_UP]: Decimal_default.ROUND_UP
};
var toFixedRounding = {
  [Rounding_default.ROUND_DOWN]: Big_default.roundDown,
  [Rounding_default.ROUND_HALF_UP]: Big_default.roundHalfUp,
  [Rounding_default.ROUND_UP]: Big_default.roundUp
};
var Fraction = class {
  /**
   * @param {BigintIsh}numerator
   * @param {BigintIsh}denominator
   */
  constructor(numerator, denominator = JSBI.BigInt(1)) {
    this.numerator = JSBI.BigInt(numerator);
    this.denominator = JSBI.BigInt(denominator);
  }
  /**
   * @param {BigintIsh | Fraction}fractionish
   * @return {Fraction}
   */
  static tryParseFraction(fractionish) {
    if (fractionish instanceof JSBI || typeof fractionish === "number" || typeof fractionish === "string")
      return new Fraction(fractionish);
    if ("numerator" in fractionish && "denominator" in fractionish)
      return fractionish;
    throw new Error("Could not parse fraction");
  }
  /** performs floor division */
  get quotient() {
    return JSBI.divide(this.numerator, this.denominator);
  }
  /** remainder after floor division*/
  get remainder() {
    return new Fraction(JSBI.remainder(this.numerator, this.denominator), this.denominator);
  }
  /**
   * @return{Fraction}
   */
  invert() {
    return new Fraction(this.denominator, this.numerator);
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  add(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    if (JSBI.equal(this.denominator, otherParsed.denominator)) {
      return new Fraction(JSBI.add(this.numerator, otherParsed.numerator), this.denominator);
    }
    return new Fraction(
      JSBI.add(
        JSBI.multiply(this.numerator, otherParsed.denominator),
        JSBI.multiply(otherParsed.numerator, this.denominator)
      ),
      JSBI.multiply(this.denominator, otherParsed.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  subtract(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    if (JSBI.equal(this.denominator, otherParsed.denominator)) {
      return new Fraction(JSBI.subtract(this.numerator, otherParsed.numerator), this.denominator);
    }
    return new Fraction(
      JSBI.subtract(
        JSBI.multiply(this.numerator, otherParsed.denominator),
        JSBI.multiply(otherParsed.numerator, this.denominator)
      ),
      JSBI.multiply(this.denominator, otherParsed.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {boolean}
   */
  lessThan(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return JSBI.lessThan(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(otherParsed.numerator, this.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {boolean}
   */
  equalTo(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return JSBI.equal(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(otherParsed.numerator, this.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {boolean}
   */
  greaterThan(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return JSBI.greaterThan(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(otherParsed.numerator, this.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  multiply(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return new Fraction(
      JSBI.multiply(this.numerator, otherParsed.numerator),
      JSBI.multiply(this.denominator, otherParsed.denominator)
    );
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Fraction}
   */
  divide(other) {
    const otherParsed = Fraction.tryParseFraction(other);
    return new Fraction(
      JSBI.multiply(this.numerator, otherParsed.denominator),
      JSBI.multiply(this.denominator, otherParsed.numerator)
    );
  }
  /**
   * @param {number}significantDigits
   * @param {object} format
   * @param {Rounding}rounding
   * @return {string}
   */
  toSignificant(significantDigits, format = { groupSeparator: "" }, rounding = Rounding_default.ROUND_HALF_UP) {
    invariant(Number.isInteger(significantDigits), `${significantDigits} is not an integer.`);
    invariant(significantDigits > 0, `${significantDigits} is not positive.`);
    Decimal_default.set({
      precision: significantDigits + 1,
      rounding: toSignificantRounding[rounding]
    });
    const quotient = new Decimal_default(this.numerator.toString()).div(this.denominator.toString()).toSignificantDigits(significantDigits);
    return quotient.toFormat(quotient.decimalPlaces(), format);
  }
  /**
   * @param {number}decimalPlaces
   * @param {object} format
   * @param {Rounding}rounding
   * @return {string}
   */
  toFixed(decimalPlaces, format = { groupSeparator: "" }, rounding = Rounding_default.ROUND_HALF_UP) {
    invariant(Number.isInteger(decimalPlaces), `${decimalPlaces} is not an integer.`);
    invariant(decimalPlaces >= 0, `${decimalPlaces} is negative.`);
    Big_default.DP = decimalPlaces;
    Big_default.RM = toFixedRounding[rounding];
    return new Big_default(this.numerator.toString()).div(this.denominator.toString()).toFormat(decimalPlaces, format);
  }
  /**
   * Helper method for converting any super class back to a fraction
   */
  get asFraction() {
    return new Fraction(this.numerator, this.denominator);
  }
};
var Fraction_default = Fraction;

// src/entities/Amount.ts
import invariant3 from "tiny-invariant";

// src/constants/numbers.ts
import JSBI3 from "jsbi";

// src/functions/percent.ts
import numeral from "numeral";
var formatPercent = (value) => {
  if (value < 1e-3) {
    return "0.00%";
  }
  return numeral(value).format("0.00%");
};

// src/entities/Percent.ts
import JSBI2 from "jsbi";
var ONE_HUNDRED = new Fraction_default(JSBI2.BigInt(100));
function toPercent(fraction) {
  return new Percent(fraction.numerator, fraction.denominator);
}
var Percent = class extends Fraction_default {
  constructor() {
    super(...arguments);
    /**
     * This boolean prevents a fraction from being interpreted as a Percent
     */
    this.isPercent = true;
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  add(other) {
    return toPercent(super.add(other));
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  subtract(other) {
    return toPercent(super.subtract(other));
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  multiply(other) {
    return toPercent(super.multiply(other));
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Percent}
   */
  divide(other) {
    return toPercent(super.divide(other));
  }
  /**
   * @param {number}significantDigits
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toSignificant(significantDigits = 5, format, rounding) {
    return super.multiply(ONE_HUNDRED).toSignificant(significantDigits, format, rounding);
  }
  /**
   * @param {number}decimalPlaces
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toFixed(decimalPlaces = 2, format, rounding) {
    return super.multiply(ONE_HUNDRED).toFixed(decimalPlaces, format, rounding);
  }
  /**
   * @param {number} decimalPlaces
   * @return {string}
   */
  toPercentageString(decimalPlaces = 2) {
    return formatPercent(super.toFixed(decimalPlaces + 2));
  }
};
var Percent_default = Percent;

// src/constants/numbers.ts
var ZERO = JSBI3.BigInt(0);
var ONE = JSBI3.BigInt(1);
var TWO = JSBI3.BigInt(2);
var THREE = JSBI3.BigInt(3);
var FIVE = JSBI3.BigInt(5);
var TEN = JSBI3.BigInt(10);
var _100 = JSBI3.BigInt(100);
var _997 = JSBI3.BigInt(997);
var _1000 = JSBI3.BigInt(1e3);
var _9994 = JSBI3.BigInt(9994);
var _9995 = JSBI3.BigInt(9995);
var _10000 = JSBI3.BigInt(1e4);
var _1e18 = JSBI3.BigInt(1e18);
var _1e12 = JSBI3.BigInt(1e12);
var _1e9 = JSBI3.BigInt(1e9);
var _1e6 = JSBI3.BigInt(1e6);
var MAX_SAFE_INTEGER = JSBI3.BigInt(Number.MAX_SAFE_INTEGER);
var MAX_UINT256 = JSBI3.BigInt(
  "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff"
);
var MAX_UINT128 = JSBI3.subtract(
  JSBI3.exponentiate(JSBI3.BigInt("2"), JSBI3.BigInt(128)),
  JSBI3.BigInt(1)
);
var ZERO_PERCENT = new Percent_default(0);

// src/entities/Share.ts
import invariant2 from "tiny-invariant";
var Share = class extends Fraction_default {
  /**
   * @param {T}currency
   * @param {BigintIsh}numerator
   * @param {BigintIsh}denominator
   */
  constructor(currency, numerator, denominator) {
    super(numerator, denominator);
    invariant2(default2.lessThanOrEqual(this.quotient, MAX_UINT128), "SHARE");
    this.currency = currency;
    this.scale = default2.exponentiate(default2.BigInt(10), default2.BigInt(currency.decimals));
  }
  /**
   * @param {T}currency
   * @param {BigintIsh}rawShare
   * @return {Share<T>}
   */
  static fromRawShare(currency, rawShare = 0) {
    return new Share(currency, rawShare);
  }
  /**
   * @param {JSBI}rebase
   * @param {noolean}roundUp
   * @return {Amount<T>}
   */
  toAmount(rebase, roundUp = false) {
    if (default2.EQ(rebase.base, ZERO))
      return Amount.fromRawAmount(this.currency, this.quotient);
    const elastic = default2.divide(default2.multiply(this.quotient, rebase.elastic), rebase.base);
    if (roundUp && default2.LT(default2.divide(default2.multiply(elastic, rebase.base), rebase.elastic), this.quotient)) {
      return Amount.fromRawAmount(this.currency, default2.add(elastic, default2.BigInt(1)));
    }
    return Amount.fromRawAmount(this.currency, elastic);
  }
  /**
   * Construct a currency share with a denominator that is not equal to 1
   * @param {T}currency the currency
   * @param {BigintIsh}numerator the numerator of the fractional token share
   * @param {BigintIsh}denominator the denominator of the fractional token share
   * @return {Share<T>}
   */
  static fromFractionalShare(currency, numerator, denominator) {
    return new Share(currency, numerator, denominator);
  }
  /**
   * @param {Share<T>}other
   * @return {Share<T>}
   */
  add(other) {
    invariant2(this.currency.equals(other.currency), "CURRENCY");
    const added = super.add(other);
    return Share.fromFractionalShare(this.currency, added.numerator, added.denominator);
  }
  /**
   * @param {Share<T>}other
   * @return {Share<T>}
   */
  subtract(other) {
    invariant2(this.currency.equals(other.currency), "CURRENCY");
    const subtracted = super.subtract(other);
    return Share.fromFractionalShare(this.currency, subtracted.numerator, subtracted.denominator);
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Share<T>}
   */
  multiply(other) {
    const multiplied = super.multiply(other);
    return Share.fromFractionalShare(this.currency, multiplied.numerator, multiplied.denominator);
  }
  /**
   * @param {Fraction | BigintIsh}other
   * @return {Share<T>}
   */
  divide(other) {
    const divided = super.divide(other);
    return Share.fromFractionalShare(this.currency, divided.numerator, divided.denominator);
  }
  /**
   * @param {number}significantDigits
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toSignificant(significantDigits = 6, format, rounding = Rounding_default.ROUND_DOWN) {
    return super.divide(this.scale).toSignificant(significantDigits, format, rounding);
  }
  /**
   * @param {number}decimalPlaces
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toFixed(decimalPlaces = this.currency.decimals, format, rounding = Rounding_default.ROUND_DOWN) {
    invariant2(decimalPlaces <= this.currency.decimals, "DECIMALS");
    return super.divide(this.scale).toFixed(decimalPlaces, format, rounding);
  }
  /**
   * @param {object}format
   * @return {string}
   */
  toExact(format = { groupSeparator: "" }) {
    Big_default.DP = this.currency.decimals;
    return new Big_default(this.quotient.toString()).div(this.scale.toString()).toFormat(format);
  }
};

// src/entities/Amount.ts
var Amount = class extends Fraction_default {
  /**
   * @param {T}currency
   * @param {BigintIsh}numerator
   * @param {BigintIsh}denominator
   */
  constructor(currency, numerator, denominator) {
    super(numerator, denominator);
    invariant3(default2.lessThanOrEqual(this.quotient, MAX_UINT256), "AMOUNT");
    this.currency = currency;
    this.scale = default2.exponentiate(default2.BigInt(10), default2.BigInt(currency.decimals));
  }
  /**
   * Returns a new currency amount instance from the unitless amount of token, i.e. the raw amount
   * @param {T}currency the currency in the amount
   * @param {BigintIsh}rawAmount the raw token or ether amount
   * @return {Amount<T>}
   */
  static fromRawAmount(currency, rawAmount) {
    return new Amount(currency, rawAmount);
  }
  /**
   * @param {T} currency
   * @param {BigintIsh} shares
   * @param {object} rebase
   * @param {boolean} roundUp
   * @return {Amount<T>}
   */
  static fromShare(currency, shares, rebase, roundUp = false) {
    if (default2.EQ(rebase.base, ZERO))
      return new Amount(currency, shares);
    const elastic = default2.divide(default2.multiply(default2.BigInt(shares), rebase.elastic), rebase.base);
    if (roundUp && default2.LT(default2.divide(default2.multiply(elastic, rebase.base), rebase.elastic), default2.BigInt(shares))) {
      return new Amount(currency, default2.add(elastic, default2.BigInt(1)));
    }
    return new Amount(currency, elastic);
  }
  /**
   * @param {object}rebase
   * @param {boolean}roundUp
   * @return {Share<T>}
   */
  toShare(rebase, roundUp = false) {
    if (default2.EQ(rebase.elastic, ZERO))
      return Share.fromRawShare(this.currency, this.quotient);
    const base = default2.divide(default2.multiply(this.quotient, rebase.base), rebase.elastic);
    if (roundUp && default2.LT(default2.divide(default2.multiply(base, rebase.elastic), rebase.base), this.quotient)) {
      return Share.fromRawShare(this.currency, default2.add(base, default2.BigInt(1)));
    }
    return Share.fromRawShare(this.currency, base);
  }
  /**
   * Construct a currency amount with a denominator that is not equal to 1
   * @param {T}currency the currency
   * @param {BigintIsh}numerator the numerator of the fractional token amount
   * @param {BigintIsh}denominator the denominator of the fractional token amount
   * @return {Amount<T>}
   */
  static fromFractionalAmount(currency, numerator, denominator) {
    return new Amount(currency, numerator, denominator);
  }
  /**
   * @param {Amount<T>} other
   * @return {Amount<T>}
   */
  add(other) {
    invariant3(this.currency.equals(other.currency), "CURRENCY");
    const added = super.add(other);
    return Amount.fromFractionalAmount(this.currency, added.numerator, added.denominator);
  }
  /**
   * @param {Amount<T>} other
   * @return {Amount<T>}
   */
  subtract(other) {
    invariant3(this.currency.equals(other.currency), "CURRENCY");
    const subtracted = super.subtract(other);
    return Amount.fromFractionalAmount(this.currency, subtracted.numerator, subtracted.denominator);
  }
  /**
   * @param {Fraction | BigintIsh} other
   * @return {Amount<T>}
   */
  multiply(other) {
    const multiplied = super.multiply(other);
    return Amount.fromFractionalAmount(this.currency, multiplied.numerator, multiplied.denominator);
  }
  /**
   * @param {Fraction | BigintIsh} other
   * @return {Amount<T>}
   */
  divide(other) {
    const divided = super.divide(other);
    return Amount.fromFractionalAmount(this.currency, divided.numerator, divided.denominator);
  }
  /**
   * @param {number}significantDigits
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toSignificant(significantDigits = 6, format, rounding = Rounding_default.ROUND_DOWN) {
    return super.divide(this.scale).toSignificant(significantDigits, format, rounding);
  }
  /**
   * @param {number}decimalPlaces
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toFixed(decimalPlaces = this.currency.decimals, format, rounding = Rounding_default.ROUND_DOWN) {
    invariant3(decimalPlaces <= this.currency.decimals, "DECIMALS");
    return super.divide(this.scale).toFixed(decimalPlaces, format, rounding);
  }
  /**
   * @return {string}
   */
  toHex() {
    return `0x${this.quotient.toString(16)}`;
  }
  /**
   * @param {object}format
   * @return {string}
   */
  toExact(format = { groupSeparator: "" }) {
    Big_default.DP = this.currency.decimals;
    return new Big_default(this.quotient.toString()).div(this.scale.toString()).toFormat(format);
  }
  /**
   */
  get wrapped() {
    if (this.currency.isToken)
      return this;
    return Amount.fromFractionalAmount(this.currency.wrapped, this.numerator, this.denominator);
  }
};

// src/entities/Price.ts
var Price = class extends Fraction_default {
  // used to adjust the raw fraction w/r/t the decimals of the {base,quote}Token
  /**
   * Construct a price, either with the base and quote currency amount, or the
   * @param {object}args
   */
  constructor(...args) {
    let baseCurrency;
    let quoteCurrency;
    let denominator;
    let numerator;
    if (args.length === 4) {
      ;
      [baseCurrency, quoteCurrency, denominator, numerator] = args;
    } else {
      const result = args[0].quoteAmount.divide(args[0].baseAmount);
      [baseCurrency, quoteCurrency, denominator, numerator] = [
        args[0].baseAmount.currency,
        args[0].quoteAmount.currency,
        result.denominator,
        result.numerator
      ];
    }
    super(numerator, denominator);
    this.baseCurrency = baseCurrency;
    this.quoteCurrency = quoteCurrency;
    this.scalar = new Fraction_default(
      default2.exponentiate(default2.BigInt(10), default2.BigInt(baseCurrency.decimals)),
      default2.exponentiate(default2.BigInt(10), default2.BigInt(quoteCurrency.decimals))
    );
  }
  /**
   * Flip the price, switching the base and quote currency
   * @return {Price<TQuote, TBase>}
   */
  invert() {
    return new Price(this.quoteCurrency, this.baseCurrency, this.numerator, this.denominator);
  }
  /**
   * Multiply the price by another price, returning a new price. The other price must have the same base currency as this price's quote currency
   * @param {Price<TQuote, TOtherQuote>}other the other price
   * @return {Price<TBase, TOtherQuote>}
   */
  multiply(other) {
    invariant4(this.quoteCurrency.equals(other.baseCurrency), "TOKEN");
    const fraction = super.multiply(other);
    return new Price(
      this.baseCurrency,
      other.quoteCurrency,
      fraction.denominator,
      fraction.numerator
    );
  }
  /**
   * Return the amount of quote currency corresponding to a given amount of the base currency
   * @param {Amount<TBase>}currencyAmount the amount of base currency to quote against the price
   * @return {Amount<TQuote>}
   */
  quote(currencyAmount) {
    invariant4(currencyAmount.currency.equals(this.baseCurrency), "TOKEN");
    const result = super.multiply(currencyAmount);
    return Amount.fromFractionalAmount(this.quoteCurrency, result.numerator, result.denominator);
  }
  /**
   * Get the value scaled by decimals for formatting
   * @private
   */
  get adjustedForDecimals() {
    return super.multiply(this.scalar);
  }
  /**
   * @param {number}significantDigits
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toSignificant(significantDigits = 6, format, rounding) {
    return this.adjustedForDecimals.toSignificant(significantDigits, format, rounding);
  }
  /**
   * @param {number}decimalPlaces
   * @param {object}format
   * @param {Rounding}rounding
   * @return {string}
   */
  toFixed(decimalPlaces = 4, format, rounding) {
    return this.adjustedForDecimals.toFixed(decimalPlaces, format, rounding);
  }
};
export {
  Price
};
//# sourceMappingURL=Price.mjs.map